import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { MapPin, Clock, Users, Star, Check, ArrowRight } from "lucide-react";
import { useEffect } from "react";

const tourHighlights = [
  "Expert local guides with deep cultural knowledge",
  "Small group sizes for personalized experiences",
  "Handpicked accommodations and authentic cuisine",
  "Flexible itineraries tailored to your interests",
  "24/7 travel support throughout your journey",
  "All-inclusive packages with no hidden fees"
];

const popularTours = [
  {
    title: "Golden Triangle India",
    destination: "Delhi, Agra, Jaipur",
    duration: "7 Days",
    groupSize: "Max 12",
    rating: 4.9,
    image: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=600&q=80"
  },
  {
    title: "European Classics",
    destination: "Paris, Rome, Barcelona",
    duration: "14 Days",
    groupSize: "Max 16",
    rating: 4.8,
    image: "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=600&q=80"
  },
  {
    title: "Southeast Asia Explorer",
    destination: "Thailand, Vietnam, Cambodia",
    duration: "12 Days",
    groupSize: "Max 14",
    rating: 4.9,
    image: "https://images.unsplash.com/photo-1528181304800-259b08848526?w=600&q=80"
  }
];

export default function GuidedToursPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white" data-testid="guided-tours-page">
      <SeoHead
        title="Guided Tour Packages"
        description="Book expert-guided tours with Tripsyfy. Cultural tours, adventure travel, and group packages across Asia, Europe, Africa, and the Americas."
        canonical="https://tripsyfy.com/services/guided-tours"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 lg:pt-28 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-accent text-sm font-semibold tracking-wide uppercase mb-4">Our Services</p>
              <h1 className="heading-xl text-primary mb-6">Guided Tours</h1>
              <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                Discover the world with expert guides who bring destinations to life. Our curated tours combine cultural immersion, authentic experiences, and seamless travel logistics.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Why Choose Our Tours */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="heading-lg text-primary mb-6">Why Choose Our Guided Tours?</h2>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  We believe travel should be transformative. Our guided tours are designed to take you beyond tourist hotspots into the heart of each destination, creating memories that last a lifetime.
                </p>
                <ul className="space-y-3">
                  {tourHighlights.map((highlight, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="h-5 w-5 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-accent" />
                      </div>
                      <span className="text-sm text-foreground">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&q=80" 
                  alt="Guided tour experience" 
                  className="rounded-lg w-full h-96 object-cover"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Popular Tours */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="heading-lg text-primary mb-4">Popular Guided Tours</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Explore our most loved tour packages, crafted for unforgettable experiences.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {popularTours.map((tour, i) => (
              <AnimatedSection key={tour.title} delay={i * 0.1}>
                <div className="bg-white border border-border rounded-lg overflow-hidden hover:border-accent/30 transition-colors group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={tour.image} 
                      alt={tour.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3 bg-white rounded-md px-2 py-1 flex items-center gap-1">
                      <Star className="h-3.5 w-3.5 fill-accent text-accent" />
                      <span className="text-xs font-semibold">{tour.rating}</span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-primary mb-2">{tour.title}</h3>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-4">
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {tour.destination}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {tour.duration}</span>
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {tour.groupSize}</span>
                    </div>
                    <Link to="/tours">
                      <Button size="sm" variant="outline" className="w-full gap-2">
                        View Details <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection>
            <div className="text-center mt-10">
              <Link to="/tours">
                <Button size="lg" className="gap-2">
                  Explore All Tours <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-lg text-primary-foreground mb-4">Ready to Start Your Adventure?</h2>
            <p className="text-primary-foreground/70 mb-8 max-w-xl mx-auto">
              Let our travel experts craft the perfect guided tour for you. Get a personalized quote today.
            </p>
            <Link to="/contact">
              <Button variant="navySolid" size="xl" className="gap-2">
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
